import React, { useState, useCallback, memo } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  styled,
  useTheme,
  Skeleton,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';

// Styled Components
const InterfaceContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
  width: '100%',
  padding: theme.spacing(4),
  backgroundColor: theme.palette.background.default,
  gap: theme.spacing(4),
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  fontSize: '2rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  textAlign: 'center',
  marginBottom: theme.spacing(2),
}));

const InputContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  maxWidth: '800px',
  backgroundColor: theme.palette.background.paper,
  borderRadius: '16px',
  padding: theme.spacing(1, 2),
  boxShadow: theme.shadows[3],
  border: `1px solid #e0e0e0`,
  gap: theme.spacing(1),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  flex: 1,
  '& .MuiOutlinedInput-root': {
    border: 'none',
    backgroundColor: 'transparent',
    '& fieldset': {
      border: 'none',
    },
    '&:hover fieldset': {
      border: 'none',
    },
    '&.Mui-focused fieldset': {
      border: 'none',
    },
  },
  '& .MuiInputBase-input': {
    fontSize: '0.95rem',
    padding: theme.spacing(1.5, 0),
    color: '#333333',
    '&::placeholder': {
      color: '#666666',
      opacity: 1,
    },
  },
}));

const SendButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: '#666666',
  color: '#ffffff',
  width: '40px',
  height: '40px',
  borderRadius: '20px',
  '&:hover': {
    backgroundColor: '#555555',
  },
  '&:disabled': {
    backgroundColor: '#cccccc',
    color: '#999999',
  },
}));

const SuggestionsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
  justifyContent: 'center',
  maxWidth: '800px',
  width: '100%',
}));

const SuggestionButton = styled(Button)(({ theme }) => ({
  borderRadius: '20px',
  padding: theme.spacing(1, 2),
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  border: `1px solid ${theme.palette.divider}`,
  textTransform: 'none',
  fontSize: '0.9rem',
  fontWeight: 500,
 
}));

interface CommonInterfaceProps {
  onSendMessage: (message: string) => void;
  onReset?: () => void;
  loading?: boolean;
  title?: string;
  suggestions?: string[];
  interfaceType?: 'homepage' | 'generate' | 'default' | 'analyze' | 'target';
  isLoadingSuggestions?: boolean;
}

// Prompt converter function for Target page
const convertToTargetPrompt = (vehicleModel: string): string => {
  // Map vehicle models to their proper target format
  const vehicleMapping: { [key: string]: string } = {
    'Honda Mixed': 'Honda Mixed',
    'Ridgeline': 'Ridgeline',
    'Civic': 'Civic',
    'CR-V': 'CR-V',
    'HR-V': 'HR-V',
    'Pilot': 'Pilot',
    'Accord': 'Accord',
    'Accord Buyers': 'Accord',
    'CR-V Buyers': 'CR-V',
    'HR-V Buyers': 'HR-V',
    'Pilot Buyers': 'Pilot',
    'Ridgeline Buyers': 'Ridgeline',
    'Civic Buyers': 'Civic',
    'Honda Mixed Buyers': 'Honda Mixed',
    'Specific VIN': 'Specific VIN',
    'Move distressed inventory': 'Distressed Inventory',
    'Distressed Inventory': 'Distressed Inventory'
  };
  
  const mappedModel = vehicleMapping[vehicleModel] || vehicleModel;
  return `Target ${mappedModel} shoppers`;
};

// Mapping for homepage suggestions to routes
const homepageSuggestionRoutes: Record<string, string> = {
  'Track campaign performance': '/ai-avatar-layout/measure',
  'Run ad-hoc campaigns': '/ai-avatar-layout/target',
  'Generate emails': '/ai-avatar-layout/generate',
  'Analyze your data': '/ai-avatar-layout/analyze',
};

const CommonInterface: React.FC<CommonInterfaceProps> = memo(({
  onSendMessage,
  onReset,
  loading = false,
  title = "How can I help you today?",
  suggestions = [
    'Track campaign performance',
    'Run ad-hoc campaigns',
    'Generate emails',
    'Analyze your data',
  ],
  interfaceType = 'default',
  isLoadingSuggestions = false,
}) => {
  const theme = useTheme();
  const history = useHistory();
  const [inputText, setInputText] = useState('');

  const handleSendMessage = useCallback(() => {
    if (inputText.trim()) {
      onSendMessage(inputText.trim());
      setInputText('');
    }
  }, [inputText, onSendMessage]);

  const handleKeyPress = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  }, [handleSendMessage]);

  const handleSuggestionClick = useCallback((suggestion: string) => {
    // If on homepage and suggestion has a route mapping, navigate to that route
    if (interfaceType === 'homepage' && homepageSuggestionRoutes[suggestion]) {
      try {
        history.push(homepageSuggestionRoutes[suggestion]);
      } catch (error) {
        console.error('Navigation error:', error);
        // Fallback to sending message if navigation fails
        onSendMessage(suggestion);
      }
      return;
    }

    // Check if this is a Target page (by checking if title contains "target")
    if (title.toLowerCase().includes('target')) {
      // Convert suggestion to target format and set in input field
      const convertedPrompt = convertToTargetPrompt(suggestion);
      onSendMessage(convertedPrompt);
    } else {
      // For other pages, send message directly
      onSendMessage(suggestion);
    }
  }, [title, onSendMessage, interfaceType, history]);

  return (
    <InterfaceContainer>
      <WelcomeText>{title}</WelcomeText>

      <InputContainer>
        <StyledTextField
          placeholder="Ask Mia anything...."
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={loading}
          multiline
          maxRows={3}
        />
        <SendButton
          onClick={handleSendMessage}
          disabled={loading || !inputText.trim()}
          size="small"
        >
          <SendIcon sx={{ fontSize: 18 }} />
        </SendButton>
      </InputContainer>

      <SuggestionsContainer>
        {isLoadingSuggestions ? (
          // Show skeleton loaders when suggestions are loading
          Array.from({ length: 6 }).map((_, index) => (
            <Skeleton
              key={`skeleton-${index}`}
              variant="rectangular"
              width={150}
              height={40}
              sx={{
                borderRadius: '20px',
              }}
            />
          ))
        ) : (
          // Show actual suggestions when loaded
          suggestions.map((suggestion, index) => (
            <SuggestionButton
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              disabled={loading}
              variant="outlined"
            >
              {suggestion}
            </SuggestionButton>
          ))
        )}
      </SuggestionsContainer>
    </InterfaceContainer>
  );
});

// Add display name for debugging
CommonInterface.displayName = 'CommonInterface';

// Export both the component and interface for flexibility
export default CommonInterface;
export { CommonInterface };
export type { CommonInterfaceProps };

/*
Usage Examples:

1. Homepage Interface:
<CommonInterface
  onSendMessage={handleSendMessage}
  onReset={handleReset}
  loading={loading}
  title="How can I help you today?"
  suggestions={[
    'Track campaign performance',
    'Run ad-hoc campaigns',
    'Generate emails',
    'Analyze your data',
  ]}
/>

2. Generate Interface:
<CommonInterface
  onSendMessage={handleSendMessage}
  onReset={handleReset}
  loading={loading}
  title="What would you like to generate?"
  suggestions={[
    'Email campaigns',
    'Social media posts',
    'Product descriptions',
    'Marketing copy',
  ]}
/>

3. Analyze Interface:
<CommonInterface
  onSendMessage={handleSendMessage}
  onReset={handleReset}
  loading={loading}
  title="What would you like to analyze?"
  suggestions={[
    'Campaign performance',
    'Customer data',
    'Sales trends',
    'ROI metrics',
  ]}
/>
*/
